import { FC, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/server';

import {
    Splide,
    SplideSlide,
    SplideTrack,
    Options
} from '@splidejs/react-splide';
import {
    ChevronLeftMedium,
    ChevronRightMedium,
    ImageSmall,
    FloorplanSmall,
    PlaySmall
} from '@zoopla-eng/zds-components/icons';
import { IconButton } from '@zoopla-eng/zds-components/button';
import {
    splideMain,
    splideTrack,
    imageStyle,
    arrow,
    slide,
    thumbnail,
    container,
    paginationPosition,
    paginationCTAPosition
} from './gallery.css';
import type {
    GalleryProps,
    Image,
    PaginationOptions,
    SlideIndex
} from './gallery.types';
import { Count } from '@zoopla-eng/zds-components/badges';

function Pagination(
    Splide: {
        on: (ev: string, fn: () => void) => void;
        index: number;
        length: number;
    },
    Components: { Elements: { track: HTMLElement } },
    options: PaginationOptions
) {
    const { track } = Components.Elements;

    let element: HTMLDivElement;

    function mount() {
        element = document.createElement('div');
        element.className = paginationPosition({
            alignment: options.alignment
        });

        track.parentElement?.insertBefore(element, track.nextSibling);

        update();
        Splide.on('move', update);
    }

    function update() {
        element.innerHTML = ReactDOM.renderToString(
            <>
                <Count
                    value={Splide.index + 1}
                    total={Splide.length}
                    icon={
                        options.Icon ? (
                            <options.Icon color="currentColor" />
                        ) : (
                            <ImageSmall color="currentColor" />
                        )
                    }
                />
                {options.numberOfFloorplans ? (
                    <Count
                        value={options.numberOfFloorplans}
                        icon={<FloorplanSmall color="currentColor" />}
                    />
                ) : null}

                {options.numberOfVideos ? (
                    <Count
                        value={options.numberOfVideos}
                        icon={<PlaySmall color="currentColor" />}
                    />
                ) : null}
            </>
        );
    }

    return {
        mount
    };
}

const hasMovedToNextSlide = ({
    active,
    previous,
    totalSlides
}: SlideIndex & { totalSlides: number }) => {
    const isLastSlide = active - totalSlides + 1 === 0;

    // Looping back to the last slide
    if (isLastSlide && previous === 0) {
        return false;
    }

    const isFirstSlide = active === 0;

    // Looping forward to the first slide
    if (isFirstSlide && previous === totalSlides - 1) {
        return true;
    }

    return active > previous;
};

export const Gallery: FC<GalleryProps> = ({
    images,
    type,
    initialSlide = 0,
    paginationAlignment = 'center',
    numberOfFloorplans = 0,
    numberOfVideos = 0,
    paginationCTA,
    objectFit,
    hideArrows,
    keyboard = true,
    Icon,
    onSlideClick,
    onSlideNext,
    onSlidePrevious,
    createCaption,
    isLazy = true,
    preloadPages = 1
}) => {
    const thumbsOptions: Options = {
        type: 'slide',
        rewind: true,
        gap: '4px',
        perPage: 4,
        pagination: false,
        height: 70,
        cover: true,
        focus: 'center',
        lazyLoad: isLazy ? 'nearby' : false,
        isNavigation: true,
        arrows: false,
        preloadPages
    };

    const [slideIndex, setSlideIndex] = useState<SlideIndex>({
        active: initialSlide,
        previous: initialSlide
    });

    const mainRef = useRef<Splide | null>(null);
    const thumbsRef = useRef<Splide | null>(null);
    const isPremium = type === 'premium';

    useEffect(() => {
        if (
            mainRef.current &&
            thumbsRef.current &&
            thumbsRef.current.splide &&
            isPremium
        ) {
            mainRef.current.sync(thumbsRef.current.splide);
        }
    }, [isPremium]);

    useEffect(() => {
        if (slideIndex.active === slideIndex.previous) return;

        const movedToNextSlide = hasMovedToNextSlide({
            active: slideIndex.active,
            previous: slideIndex.previous,
            totalSlides: images.length
        });

        if (onSlideNext && movedToNextSlide) {
            onSlideNext(slideIndex.active);
            return;
        }

        if (onSlidePrevious) onSlidePrevious(slideIndex.active);
    }, [
        slideIndex.active,
        slideIndex.previous,
        images.length,
        onSlideNext,
        onSlidePrevious
    ]);

    return (
        <div className={container}>
            <Splide
                ref={mainRef}
                hasTrack={false}
                className={splideMain}
                // aria-label="My Gallery"
                options={{
                    start: initialSlide,
                    type: 'loop',
                    gap: '4px',
                    pagination: false,
                    keyboard,
                    lazyLoad: isLazy ? 'nearby' : false,
                    arrows: images.length > 1,
                    drag: images.length > 1,
                    preloadPages
                }}
                extensions={{
                    pagination: (Splide, Components) =>
                        Pagination(Splide, Components, {
                            alignment: paginationCTA
                                ? 'right'
                                : paginationAlignment,
                            numberOfFloorplans,
                            numberOfVideos,
                            Icon
                        })
                }}
                onMoved={(_splide, index, prev) =>
                    (onSlideNext || onSlidePrevious) &&
                    setSlideIndex({ active: index, previous: prev })
                }
            >
                <SplideTrack className={splideTrack}>
                    {images?.map((image: Image, imgIndex: number) => (
                        <SplideSlide
                            key={imgIndex}
                            className={slide}
                            onClick={() => onSlideClick?.(imgIndex)}
                        >
                            {image.sources ? (
                                <picture>
                                    {image.sources?.map((source, indx) => (
                                        <source
                                            data-splide-lazy-srcset={
                                                isLazy || imgIndex !== 0
                                                    ? source.srcSet
                                                    : undefined
                                            }
                                            srcSet={
                                                !isLazy || imgIndex === 0
                                                    ? source.srcSet
                                                    : undefined
                                            }
                                            type={
                                                source.type === 'original'
                                                    ? undefined
                                                    : source.type
                                            }
                                            media={source.media}
                                            sizes={source.sizes ?? image.sizes}
                                            key={`${source.type}${indx}`}
                                        />
                                    ))}
                                    <img
                                        className={imageStyle({ objectFit })}
                                        alt={createCaption(
                                            imgIndex,
                                            images.length
                                        )}
                                        // note that we purposefully omit img.src to stop safari & firefox double fetching and always use a lazy src to keep splide happy
                                        data-splide-lazy={
                                            isLazy ? image.src : undefined
                                        }
                                        src={isLazy ? '' : undefined}
                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore
                                        fetchPriority={
                                            image.fetchPriority === 'high' &&
                                            imgIndex === 0
                                                ? 'high'
                                                : image.fetchPriority ?? 'auto'
                                        }
                                    />
                                </picture>
                            ) : (
                                <img
                                    className={imageStyle({ objectFit })}
                                    data-splide-lazy-srcset={image.srcSet}
                                    src=""
                                    sizes={image.sizes}
                                    alt={createCaption(imgIndex, images.length)}
                                    data-splide-lazy={image.src}
                                />
                            )}
                        </SplideSlide>
                    ))}
                </SplideTrack>

                <div className="splide__arrows">
                    <div
                        className={`splide__arrow splide__arrow--prev ${arrow({
                            hideArrows
                        })}`}
                    >
                        <IconButton
                            size="narrow"
                            category="primary"
                            contrasted
                            label="Previous slide"
                            onClick={(event) => event.preventDefault()}
                        >
                            {ChevronLeftMedium}
                        </IconButton>
                    </div>
                    <div
                        className={`splide__arrow splide__arrow--next ${arrow({
                            hideArrows
                        })}`}
                    >
                        <IconButton
                            size="narrow"
                            category="primary"
                            contrasted
                            label="Next slide"
                            onClick={(event) => event.preventDefault()}
                        >
                            {ChevronRightMedium}
                        </IconButton>
                    </div>
                </div>
                {paginationCTA && (
                    <div className={paginationCTAPosition}>{paginationCTA}</div>
                )}
            </Splide>
            {isPremium && (
                <Splide
                    options={thumbsOptions}
                    ref={thumbsRef}
                    // aria-label="The carousel with thumbnails. Selecting a thumbnail will change the main carousel"
                >
                    {images?.map((image: Image, index: number) => (
                        <SplideSlide
                            key={index}
                            className={thumbnail}
                            onClick={(event) => event.preventDefault()}
                        >
                            <img
                                data-splide-lazy={image.src}
                                src=""
                                alt={`Thumbnail ${createCaption(
                                    index,
                                    images.length
                                )}`}
                            />
                        </SplideSlide>
                    ))}
                </Splide>
            )}
        </div>
    );
};

export default Gallery;
